import type { CmChecklist } from '@luxuryescapes/contract-svc-offer';

import type { CampaignRowView } from '../../tabs/campaign/types';

type CampaignField = CampaignRowView['field'];

export const CAMPAIGN_RULE_TEXT: Record<
  CmChecklist.CampaignRuleId,
  { field: CampaignField; rule: string; source: string }
> = {
  'campaign-payment-terms-modification': {
    field: 'Payment Terms Modification',
    rule: 'Auto-approve when the field is blank.',
    source: 'Opportunity contract payment terms modification fields',
  },
  'campaign-contract-terms-modification': {
    field: 'Contract Terms Modification',
    rule: 'Auto-approve when the field is blank.',
    source: 'Opportunity contract special_comments_notes__c',
  },
  'campaign-date-change-allowed': {
    field: 'Date change allowed',
    rule: 'Loaded value must match the Policy Bible.',
    source: 'Opportunity functional details: days_before_check_in_changes_disallowed__c',
  },
  'campaign-date-change-prior-days': {
    field: 'Days prior to check-in for date changes',
    rule: 'Loaded value must match the Policy Bible.',
    source: 'Opportunity functional details: days_before_check_in_changes_disallowed__c',
  },
  'campaign-number-date-changes': {
    field: 'Number of date changes',
    rule: 'Loaded value must match the Policy Bible.',
    source: 'Opportunity functional details: number_of_date_changes__c',
  },
  'campaign-booking-cutoff': {
    field: 'Booking data cut-off',
    rule: 'If booking data cut-off is blank, populate = 2.',
    source: 'Opportunity functional details / offer booking_min_lead_days',
  },
  'campaign-package-upgrades': {
    field: 'Package upgrades allowed',
    rule: 'Flag for approval when package upgrades are not allowed.',
    source: 'Opportunity functional details / offer package_upgrades_allowed',
  },
  'campaign-sem-support': {
    field: 'SEM support',
    rule: 'Flag for approval only when SEM support is Yes.',
    source: 'Opportunity key commercial no_sem_support_for_this_deal__c / offer sf_no_sem_support',
  },
  'campaign-type': {
    field: 'Campaign type',
    rule: 'Auto-approve when campaign type matches FLASH - flexi nights or FLASH - standard, based on whether flexi deal options are loaded.',
    source: 'Opportunity info campaign_type__c / offer campaign_type',
  },
  'campaign-book-by-date': {
    field: 'Book By date',
    rule: 'Auto-approve when Book By is 25 days to 2 months before Travel To.',
    source: 'Opportunity key dates / offer book_by_date and travel_to_date',
  },
  'campaign-flexi-deal-options': {
    field: 'Flexi night + deal options',
    rule: 'If flexi night, need at least 1 deal option with flexi night. If standard, no deal options with flexi night.',
    source: 'Opportunity info campaign_type__c / package flexible_nights',
  },
  'campaign-flexi-night-margin': {
    field: 'Flexi night margin',
    rule: 'If any flexi night margin is below 10% or above 40%, flag for approval; otherwise auto-approve.',
    source: 'Opportunity deal extra_night_margin__c / package extra_night_margin',
  },
  'campaign-suggested-value': {
    field: 'Suggested value',
    rule: 'Compare Suggested value with Value in cost currency.',
    source: 'Opportunity deal suggested_value__c / offer package price value',
  },
};

export function getCampaignSummary(field: CampaignField, approved: boolean) {
  switch (field) {
    case 'Date change allowed':
      return approved
        ? 'Date change allowance matches the policy bible.'
        : 'Date change allowance needs policy bible review.';
    case 'Days prior to check-in for date changes':
      return approved
        ? 'Days prior to check-in for date changes matches policy bible.'
        : 'Days prior to check-in for date changes needs policy bible review.';
    case 'Number of date changes':
      return approved
        ? 'Number of permitted date changes matches the policy bible.'
        : 'Number of permitted date changes needs policy bible review.';
    case 'Booking data cut-off':
      return approved
        ? 'Booking data cut-off is populated.'
        : 'Booking data cut-off is blank and should be populated to 2.';
    case 'Package upgrades allowed':
      return approved ? 'Package upgrades are allowed.' : 'Package upgrades need review.';
    case 'SEM support':
      return approved ? 'SEM support is not enabled.' : 'SEM support is enabled.';
    case 'Campaign type':
      return approved ? 'Campaign type is a supported Flash type.' : 'Campaign type needs review.';
    case 'Contract Terms Modification':
      return approved
        ? 'Contract terms modification field is blank.'
        : 'Contract terms modification field is populated.';
    case 'Payment Terms Modification':
      return approved ? 'Payment Terms Modification is blank.' : 'Payment Terms Modification is enabled.';
    case 'Book By date':
      return approved ? 'Book By is approximately one month before Travel To.' : 'Book By date needs review.';
    case 'Suggested value':
      return approved
        ? 'Suggested value equals value in cost currency for every deal option.'
        : 'Suggested value needs review.';
    default:
      return approved ? 'Campaign row is approved.' : 'Campaign row needs review.';
  }
}

export const DATE_CHANGES_DISALLOWED_EVIDENCE =
  'Date changes are not allowed for this offer, so there is no number of days to compare.';
export const SOURCE_UNAVAILABLE_EVIDENCE =
  'The source data for this rule could not be loaded, so it cannot be checked.';
export const SOURCE_UNAVAILABLE_VALUE = 'Unavailable';

export function getPolicyBibleEvidence({
  approved,
  expected,
  hasRule,
  loaded,
}: {
  approved: boolean;
  expected: number | string | null;
  hasRule: boolean;
  loaded: number | string | null;
}) {
  if (!hasRule) {
    return 'Policy Bible expected value is not available for the loaded chain and geo.';
  }

  if (expected == null || expected === '') {
    return 'Policy Bible expected value is blank or ambiguous for the loaded chain and geo.';
  }

  if (loaded == null || loaded === '') {
    return 'Loaded value is missing.';
  }

  return approved ? 'Loaded value matches the Policy Bible.' : 'Loaded value does not match the Policy Bible.';
}

export function formatStringValue(value?: string | number | boolean | null, fallback = 'Not available') {
  if (value == null || value === '') {
    return fallback;
  }

  return String(value);
}
